import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { Loader2, Upload, XCircle } from "lucide-react";

import { Button } from "@/lib/shadcn/ui/button";
import { Badge } from "@/lib/shadcn/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/lib/shadcn/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import { SearchableSelect, SelectOption } from "@/lib/shadcn/components/searchable-select";
import FileUploadDialog from "@/lib/shadcn/components/file-upload-dialog";
import {
    CancelledOnlyElement,
    ExcelReservationRow,
    MatchedDifference,
    ReconciliationResult,
} from "@/models/responses";
import { useReconcileMutation } from "@/store/hosthub-api";

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const monthOptions: SelectOption[] = MONTHS.map((m) => ({ value: m, label: m }));

function yearOptions(): SelectOption[] {
    const current = new Date().getFullYear();
    const years: SelectOption[] = [];
    for (let y = current; y >= current - 6; y--) {
        years.push({ value: String(y), label: String(y) });
    }
    return years;
}

const READONLY_BANNER =
    "Read-only comparison — nothing is saved. Fix discrepancies in HostHub (or your spreadsheet).";

/** Render the date columns of an Excel/HostHub row compactly. */
function rowSummaryCells(code: string, apartmentId: number): React.ReactNode {
    return (
        <>
            <TableCell className="font-medium">{code}</TableCell>
            <TableCell>{apartmentId}</TableCell>
        </>
    );
}

interface DifferingTableProps {
    rows: MatchedDifference[];
}

/**
 * ReconciliationDiffTable — the Differing bucket. For each matched reservation
 * whose fields disagree, one table row per differing field with two columns
 * (Excel | HostHub). The differing cell is marked with a textual ≠ marker (not
 * color-only) per ui-spec accessibility.
 */
const ReconciliationDiffTable: React.FC<DifferingTableProps> = ({ rows }) => (
    <div className="overflow-x-auto">
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Reservation</TableHead>
                    <TableHead>Apartment</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>Excel</TableHead>
                    <TableHead>HostHub</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.flatMap((match) =>
                    match.differences.map((diff) => (
                        <TableRow
                            key={`${match.excel.reservationCode}-${diff.field}`}
                            data-testid={`diff-row-${match.excel.reservationCode}-${diff.field}`}
                        >
                            {rowSummaryCells(match.excel.reservationCode, match.excel.apartmentId)}
                            <TableCell>{diff.field}</TableCell>
                            <TableCell className="bg-amber-50">
                                <span
                                    data-testid="diff-marker"
                                    role="img"
                                    aria-label="differs"
                                    className="mr-1 font-semibold text-amber-700"
                                >
                                    ≠
                                </span>
                                {diff.excelValue}
                            </TableCell>
                            <TableCell className="bg-amber-50">{diff.hosthubValue}</TableCell>
                        </TableRow>
                    )),
                )}
            </TableBody>
        </Table>
    </div>
);

const ExcelOnlyTable: React.FC<{ rows: ExcelReservationRow[] }> = ({ rows }) => (
    <div className="overflow-x-auto">
        <p className="mb-2 text-sm text-muted-foreground">Not in HostHub — add it there if it&apos;s a real booking.</p>
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Reservation</TableHead>
                    <TableHead>Apartment</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Nights</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.reservationCode} data-testid={`excel-only-row-${row.reservationCode}`}>
                        {rowSummaryCells(row.reservationCode, row.apartmentId)}
                        <TableCell>{row.guestName}</TableCell>
                        <TableCell>{row.nights}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </div>
);

const HosthubOnlyTable: React.FC<{ rows: CancelledOnlyElement[] }> = ({ rows }) => (
    <div className="overflow-x-auto">
        <p className="mb-2 text-sm text-muted-foreground">Missing from your spreadsheet.</p>
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Reservation</TableHead>
                    <TableHead>Apartment</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Nights</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.id} data-testid={`hosthub-only-row-${row.reservationCode}`}>
                        {rowSummaryCells(row.reservationCode, row.apartmentId)}
                        <TableCell>{row.guestName}</TableCell>
                        <TableCell>{row.actualDaysOfStay}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </div>
);

const CancelledOnlyTable: React.FC<{ rows: CancelledOnlyElement[] }> = ({ rows }) => (
    <div className="overflow-x-auto">
        <p className="mb-2 text-sm text-muted-foreground">
            Cancelled in HostHub — not a discrepancy. These won&apos;t be in your active spreadsheet.
        </p>
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Reservation</TableHead>
                    <TableHead>Apartment</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Nights</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.id} data-testid={`cancelled-only-row-${row.reservationCode}`}>
                        {rowSummaryCells(row.reservationCode, row.apartmentId)}
                        <TableCell>{row.guestName}</TableCell>
                        <TableCell>{row.actualDaysOfStay}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </div>
);

function isEmptyResult(result: ReconciliationResult): boolean {
    return (
        result.matchedDiffering.length === 0 &&
        result.excelOnly.length === 0 &&
        result.hosthubOnly.length === 0 &&
        result.cancelledOnly.length === 0 &&
        result.matchedEqual.length === 0
    );
}

/**
 * ReconciliationPage (ui-spec Screen 4) — read-only Excel-vs-HostHub comparison
 * for a month. Nothing is persisted server-side. The operator picks a year +
 * month, uploads a spreadsheet, and reviews four disclosure sections (Differing
 * open by default). Differences are surfaced with a textual ≠ marker so colour
 * is never the only signal.
 */
const ReconciliationPage: React.FC = () => {
    const now = new Date();
    const [year, setYear] = useState<number>(now.getFullYear());
    const [month, setMonth] = useState<string>(MONTHS[now.getMonth()]);
    const [uploadOpen, setUploadOpen] = useState(false);

    const [reconcile, { data, isLoading, isError, error }] = useReconcileMutation();

    const handleUpload = useCallback(
        (file: File) => {
            void reconcile({ file, month, year });
        },
        [reconcile, month, year],
    );

    const parseErrorMessage = useMemo(() => {
        if (!isError || !error) return "";
        const e = error as { data?: { errors?: { message: string }[] } };
        const messages = e.data?.errors?.map((er) => er.message).filter(Boolean) ?? [];
        return messages.join(", ");
    }, [isError, error]);

    const result = data?.result;
    const showEmptyMonth = !isLoading && !isError && result !== undefined && isEmptyResult(result);
    const showResults = !isLoading && !isError && result !== undefined && !isEmptyResult(result);

    return (
        <div data-testid="reconciliation-page" className="flex flex-col gap-4">
            <h1 className="text-3xl font-bold">Reconciliation</h1>
            <div className="flex flex-wrap items-center gap-3">
                <Button data-testid="reconciliation-open-upload" onClick={() => setUploadOpen(true)}>
                    <Upload className="mr-1 h-4 w-4" aria-hidden="true" />
                    Upload spreadsheet
                </Button>
                <div className="flex items-center gap-2">
                    <label htmlFor="reconciliation-year" className="text-sm text-muted-foreground">
                        Year
                    </label>
                    <SearchableSelect
                        id="reconciliation-year"
                        options={yearOptions()}
                        placeholder="Year"
                        selectedValue={String(year)}
                        onChange={(value) => setYear(Number(value))}
                        variant="outline"
                        width="w-[120px]"
                        className="reconciliation-year-select"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <label htmlFor="reconciliation-month" className="text-sm text-muted-foreground">
                        Month
                    </label>
                    <SearchableSelect
                        id="reconciliation-month"
                        options={monthOptions}
                        placeholder="Month"
                        selectedValue={month}
                        onChange={(value) => setMonth(value)}
                        variant="outline"
                        width="w-[160px]"
                        className="reconciliation-month-select"
                    />
                </div>
            </div>

            <div
                data-testid="reconciliation-banner"
                role="status"
                className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-800"
            >
                {READONLY_BANNER}
            </div>

            {isLoading && (
                <div
                    data-testid="reconciliation-parsing"
                    role="status"
                    aria-live="polite"
                    className="flex items-center gap-2 py-8 text-sm text-muted-foreground"
                >
                    <Loader2 className="h-5 w-5 motion-safe:animate-spin" aria-hidden="true" />
                    Reading spreadsheet…
                </div>
            )}

            {isError && (
                <div
                    data-testid="reconciliation-error-panel"
                    role="alert"
                    className="flex flex-col items-center gap-4 py-8 text-center"
                >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <XCircle className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <p className="max-w-md text-sm text-slate-700">
                        The spreadsheet could not be processed{parseErrorMessage ? `: ${parseErrorMessage}` : "."}
                    </p>
                </div>
            )}

            {!isLoading && !isError && result === undefined && (
                <div data-testid="reconciliation-initial" className="py-12 text-center text-sm text-muted-foreground">
                    Upload your spreadsheet to compare it against HostHub for a month.
                </div>
            )}

            {showEmptyMonth && (
                <div data-testid="reconciliation-empty-month" className="py-12 text-center text-sm text-muted-foreground">
                    No reservations for {month} {year} on either side.
                </div>
            )}

            {showResults && result && (
                <div data-testid="reconciliation-results" className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2 text-sm">
                        <Badge data-testid="summary-differing" variant="secondary">
                            Differing {result.matchedDiffering.length}
                        </Badge>
                        <Badge data-testid="summary-excel-only" variant="secondary">
                            Excel-only {result.excelOnly.length}
                        </Badge>
                        <Badge data-testid="summary-hosthub-only" variant="secondary">
                            HostHub-only {result.hosthubOnly.length}
                        </Badge>
                        <Badge data-testid="summary-cancelled-only" variant="secondary">
                            Cancelled (HostHub-only) {result.cancelledOnly.length}
                        </Badge>
                        <Badge data-testid="summary-matched" variant="secondary">
                            Matched {result.matchedEqual.length}
                        </Badge>
                    </div>

                    <Accordion type="multiple" defaultValue={["differing"]}>
                        <AccordionItem value="differing" data-testid="section-differing">
                            <AccordionTrigger>Differing ({result.matchedDiffering.length})</AccordionTrigger>
                            <AccordionContent>
                                {result.matchedDiffering.length > 0 ? (
                                    <ReconciliationDiffTable rows={result.matchedDiffering} />
                                ) : (
                                    <p className="text-sm text-muted-foreground">No differing reservations.</p>
                                )}
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="excel-only" data-testid="section-excel-only">
                            <AccordionTrigger>Excel-only ({result.excelOnly.length})</AccordionTrigger>
                            <AccordionContent>
                                {result.excelOnly.length > 0 ? (
                                    <ExcelOnlyTable rows={result.excelOnly} />
                                ) : (
                                    <p className="text-sm text-muted-foreground">Nothing only in the spreadsheet.</p>
                                )}
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="hosthub-only" data-testid="section-hosthub-only">
                            <AccordionTrigger>HostHub-only ({result.hosthubOnly.length})</AccordionTrigger>
                            <AccordionContent>
                                {result.hosthubOnly.length > 0 ? (
                                    <HosthubOnlyTable rows={result.hosthubOnly} />
                                ) : (
                                    <p className="text-sm text-muted-foreground">Nothing only in HostHub.</p>
                                )}
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="cancelled-only" data-testid="section-cancelled-only">
                            <AccordionTrigger>
                                Cancelled (HostHub-only) ({result.cancelledOnly.length})
                            </AccordionTrigger>
                            <AccordionContent>
                                {result.cancelledOnly.length > 0 ? (
                                    <CancelledOnlyTable rows={result.cancelledOnly} />
                                ) : (
                                    <p className="text-sm text-muted-foreground">No cancelled reservations.</p>
                                )}
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="matched-equal" data-testid="section-matched-equal">
                            <AccordionTrigger>Matched ({result.matchedEqual.length})</AccordionTrigger>
                            <AccordionContent>
                                <ul className="space-y-1 text-sm">
                                    {result.matchedEqual.map((pair) => (
                                        <li key={pair.hosthub.id} data-testid={`matched-row-${pair.hosthub.reservationCode}`}>
                                            {pair.hosthub.reservationCode}
                                        </li>
                                    ))}
                                </ul>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            )}

            <FileUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onUpload={handleUpload} />
        </div>
    );
};

export { ReconciliationDiffTable };
export default ReconciliationPage;
